import { Id } from '@/infra/id'
import type { PersonalProfileVersion } from '@/features/tax-profile/individual-profiles/PersonalProfile'
import type { SpousalProfileVersion } from '@/features/tax-profile/individual-profiles/SpousalProfile'
import type { DependentProfileVersion } from '@/features/tax-profile/individual-profiles/DependentProfile'
import type { JobsApi } from '@/features/jobs/api/job-api'
import {
  GetChecklistRequest,
  ProvideSupportingItemPersonalInfoRequest,
  ProvideSupportingItemSpousalInfoRequest,
  type RequestedSupportingItemUpdatedRealtimeEvent as RequestedSupportingItemUpdatedRealtimeEventProto,
  ProvideSupportingItemDependentInfoRequest,
  RemoveSupportingItemDependentInfoRequest,
  ProvideSupportingItemPersonalRefundMethodRequest,
  ProvideSupportingItemBusinessInfoRequest,
  ProvideSupportingItemBusinessRefundMethodRequest,
  ProvideSupportingItemDocumentRequest,
  RemoveSupportingItemDocumentRequest,
  ProvideSupportingItemNotesRequest,
} from '@taxfyle/api-internal/internal/info_gathering_pb'
import {
  mapChecklistProtoToProps,
  mapRequestedSupportingItemProtoToProps,
} from '@/features/jobs/details/tax-type/info-gathering/info-gathering-mappers'
import { collection } from '@/infra/collection'
import { Checklist } from '@/features/jobs/details/tax-type/info-gathering/Checklist'
import { onRpcNotFoundReturnNull } from '@/utils/grpc-util'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { map, share } from 'rxjs'
import { filterNonNil } from '@/utils/rx-util'
import { clearMemoized } from '@/utils/memoize-util'
import type { RequestedSupportingItem } from './RequestedSupportingItem'
import type { PersonalInfoSupportingItemInput } from './personal-info/PersonalInfoSupportingItemInput'
import type { SpousalInfoSupportingItemInput } from './spousal-info/SpousalInfoSupportingItemInput'
import type { DependentsInfoSupportingItemInput } from './dependents-info/DependentsInfoSupportingItemInput'
import { action } from 'mobx'
import type { PersonalTaxRefundMethodSupportingItemInput } from './refund-methods/PersonalTaxRefundMethodSupportingItemInput'
import type { TaxRefundMethodVersion } from '@/features/tax-profile/tax-refund-methods/TaxRefundMethod'
import type { BusinessProfileVersion } from '@/features/tax-profile/business-profiles/BusinessProfile'
import type { BusinessInfoSupportingItemInput } from './business-info/BusinessInfoSupportingItemInput'
import type { BusinessTaxRefundMethodSupportingItemInput } from './refund-methods/BusinessTaxRefundMethodSupportingItemInput'
import type { DocumentSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/documents/DocumentSupportingItemInput'
import type { NotesSupportingItemInput } from './notes/NotesSupportingItemInput'
import { tap } from 'rxjs/operators'
import type { DocumentRequestSubmittedRealtimeEvent } from '@taxfyle/api-internal/internal/document_request_pb'
import { delay } from 'fejl'
import { CompleteInfoGatheringRequest } from '@taxfyle/api-internal/internal/job_pb'
import type { SubmittedJobStore } from '@/features/jobs/details/SubmittedJobStore'

/**
 * Manages info gathering for a job.
 */
export class InfoGatheringStore {
  readonly checklists = collection({
    create: () => new Checklist(),
    getModelId: (m) => m.jobId,
    getDataId: (d) => d.jobId,
  })

  /**
   * Emits when items are updated within a checklist.
   */
  readonly itemUpdates$

  constructor(
    private readonly _api: JobsApi,
    private readonly _submittedJobStore: SubmittedJobStore,
  ) {
    this.itemUpdates$ = this._api.realtime$.pipe(
      map((e) => e.getRequestedSupportingItemUpdated()),
      filterNonNil(),
      map(this._onRemoteUpdate),
      filterNonNil(),
      share(),
    )

    this.itemUpdates$.subscribe()

    // Reload the checklist when a document request comes in.
    this._api.realtime$
      .pipe(
        map((e) => e.getDocumentRequestCreated()),
        filterNonNil(),
        tap(this._onRemoteDocumentRequestCreated),
      )
      .subscribe()
  }

  /**
   * Fetches the checklist for a job.
   */
  @memoizeAsync()
  async fetchChecklist(
    jobId: Id,
    signal?: AbortSignal,
  ): Promise<Checklist | null> {
    signal?.throwIfAborted()
    jobId = Id.assert(jobId)

    return await this._api.infoGathering
      .getChecklist(new GetChecklistRequest().setJobId(jobId))
      .then((response) => {
        signal?.throwIfAborted()

        const props = mapChecklistProtoToProps(jobId, response)
        return this.checklists.set(props)
      })
      .catch(onRpcNotFoundReturnNull)
  }

  /**
   * Provides a personal profile version to a job.
   */
  async providePersonalProfile(
    jobId: Id,
    requestedSupportingItem: RequestedSupportingItem<PersonalInfoSupportingItemInput>,
    profileVersion: PersonalProfileVersion,
  ) {
    jobId = Id.assert(jobId)
    const requestedSupportingItemId = Id.assert(requestedSupportingItem.id)
    const profileId = Id.assert(profileVersion.id)
    const version = profileVersion.version

    await this._api.infoGathering.provideSupportingItemPersonalInfo(
      new ProvideSupportingItemPersonalInfoRequest()
        .setJobId(jobId)
        .setId(requestedSupportingItemId)
        .setProfileId(Id.assert(profileId))
        .setProfileVersion(version),
    )

    // Optimistically update the checklist item.
    requestedSupportingItem.input.set({
      provided: {
        profileId: profileId,
        profileVersion: version,
      },
    })

    // Refresh the checklist.
    void this._refreshChecklist(jobId)
  }

  /**
   * Provides a spousal profile version to a job.
   */
  async provideSpousalProfile(
    jobId: Id,
    requestedSupportingItem: RequestedSupportingItem<SpousalInfoSupportingItemInput>,
    profileVersion: SpousalProfileVersion,
  ) {
    jobId = Id.assert(jobId)
    const requestedSupportingItemId = Id.assert(requestedSupportingItem.id)
    const profileId = Id.assert(profileVersion.id)
    const version = profileVersion.version

    await this._api.infoGathering.provideSupportingItemSpousalInfo(
      new ProvideSupportingItemSpousalInfoRequest()
        .setJobId(jobId)
        .setId(requestedSupportingItemId)
        .setProfileId(Id.assert(profileId))
        .setProfileVersion(version),
    )

    // Optimistically update the checklist item.
    requestedSupportingItem.input.set({
      provided: {
        profileId: profileId,
        profileVersion: version,
      },
    })

    // Refresh the checklist.
    void this._refreshChecklist(jobId)
  }

  /**
   * Provides dependent profiles to a job.
   */
  async provideDependent(
    jobId: Id,
    requestedSupportingItem: RequestedSupportingItem<DependentsInfoSupportingItemInput>,
    profileVersion: DependentProfileVersion,
  ) {
    jobId = Id.assert(jobId)
    const requestedSupportingItemId = Id.assert(requestedSupportingItem.id)
    const profileId = Id.assert(profileVersion.id)
    const version = profileVersion.version

    await this._api.infoGathering.provideSupportingItemDependentInfo(
      new ProvideSupportingItemDependentInfoRequest()
        .setJobId(jobId)
        .setId(requestedSupportingItemId)
        .setProfileId(profileId)
        .setProfileVersion(version),
    )

    // Optimistically update the checklist item.
    requestedSupportingItem.input.addDependent({
      profileId: profileId,
      profileVersion: version,
    })

    // Refresh the checklist.
    void this._refreshChecklist(jobId)
  }

  /**
   * Removes a dependent from a job.
   */
  async removeDependent(
    jobId: Id,
    requestedSupportingItem: RequestedSupportingItem<DependentsInfoSupportingItemInput>,
    profileId: Id,
  ) {
    jobId = Id.assert(jobId)
    const requestedSupportingItemId = Id.assert(requestedSupportingItem.id)
    const profileIdToRemove = Id.assert(profileId)

    await this._api.infoGathering.removeSupportingItemDependentInfo(
      new RemoveSupportingItemDependentInfoRequest()
        .setJobId(jobId)
        .setId(requestedSupportingItemId)
        .setProfileId(profileIdToRemove),
    )

    // Optimistically update the checklist item.
    requestedSupportingItem.input.removeDependent(profileIdToRemove)

    // Refresh the checklist.
    void this._refreshChecklist(jobId)
  }

  /**
   * Provides a personal tax refund method to a job.
   */
  async providePersonalTaxRefundMethod(
    jobId: Id,
    requestedSupportingItem: RequestedSupportingItem<PersonalTaxRefundMethodSupportingItemInput>,
    methodVersion: TaxRefundMethodVersion,
  ) {
    jobId = Id.assert(jobId)
    const requestedSupportingItemId = Id.assert(requestedSupportingItem.id)
    const refundMethodId = Id.assert(methodVersion.id)
    const refundMethodVersion = methodVersion.version

    await this._api.infoGathering.provideSupportingItemPersonalRefundMethod(
      new ProvideSupportingItemPersonalRefundMethodRequest()
        .setJobId(jobId)
        .setId(requestedSupportingItemId)
        .setRefundMethodId(refundMethodId)
        .setRefundMethodVersion(refundMethodVersion),
    )

    // Optimistically update the checklist item.
    requestedSupportingItem.input.set({
      provided: {
        refundMethodId,
        refundMethodVersion,
      },
    })

    // Refresh the checklist.
    void this._refreshChecklist(jobId)
  }

  /**
   * Provides a business profile to a job.
   */
  async provideBusinessProfile(
    jobId: Id,
    requestedSupportingItem: RequestedSupportingItem<BusinessInfoSupportingItemInput>,
    profileVersion: BusinessProfileVersion,
  ) {
    jobId = Id.assert(jobId)
    const requestedSupportingItemId = Id.assert(requestedSupportingItem.id)
    const profileId = Id.assert(profileVersion.id)
    const version = profileVersion.version

    await this._api.infoGathering.provideSupportingItemBusinessInfo(
      new ProvideSupportingItemBusinessInfoRequest()
        .setJobId(jobId)
        .setId(requestedSupportingItemId)
        .setProfileId(Id.assert(profileId))
        .setProfileVersion(version),
    )

    // Optimistically update the checklist item.
    requestedSupportingItem.input.set({
      provided: {
        profileId: profileId,
        profileVersion: version,
      },
    })

    // Refresh the checklist.
    void this._refreshChecklist(jobId)
  }

  /**
   * Provides a business tax refund method to a job.
   */
  async provideBusinessTaxRefundMethod(
    jobId: Id,
    requestedSupportingItem: RequestedSupportingItem<BusinessTaxRefundMethodSupportingItemInput>,
    methodVersion: TaxRefundMethodVersion,
  ) {
    jobId = Id.assert(jobId)
    const requestedSupportingItemId = Id.assert(requestedSupportingItem.id)
    const refundMethodId = Id.assert(methodVersion.id)
    const refundMethodVersion = methodVersion.version

    await this._api.infoGathering.provideSupportingItemBusinessRefundMethod(
      new ProvideSupportingItemBusinessRefundMethodRequest()
        .setJobId(jobId)
        .setId(requestedSupportingItemId)
        .setRefundMethodId(refundMethodId)
        .setRefundMethodVersion(refundMethodVersion),
    )

    // Optimistically update the checklist item.
    requestedSupportingItem.input.set({
      provided: {
        refundMethodId,
        refundMethodVersion,
      },
    })

    // Refresh the checklist.
    void this._refreshChecklist(jobId)
  }

  /**
   * Provides a document to a supporting item.
   *
   * @param jobId
   * @param requestedSupportingItem
   * @param documentLongId
   */
  async provideDocument(
    jobId: Id,
    requestedSupportingItem: RequestedSupportingItem<DocumentSupportingItemInput>,
    documentLongId: Id,
  ) {
    jobId = Id.assert(jobId)

    const requestedSupportingItemId = Id.assert(requestedSupportingItem.id)
    const documentLongIdToProvide = Id.assert(documentLongId)

    await this._api.infoGathering.provideSupportingItemDocument(
      new ProvideSupportingItemDocumentRequest()
        .setJobId(jobId)
        .setId(requestedSupportingItemId)
        .setDocumentLongId(documentLongIdToProvide),
    )

    // Optimistically update the checklist item.
    requestedSupportingItem.input.addDocument(documentLongIdToProvide)

    // Refresh the checklist.
    void this._refreshChecklist(jobId)
  }

  /**
   * Removes a document from a supporting item.
   * @param jobId
   * @param requestedSupportingItem
   * @param documentLongId
   */
  async removeDocument(
    jobId: Id,
    requestedSupportingItem: RequestedSupportingItem<DocumentSupportingItemInput>,
    documentLongId: Id,
  ) {
    jobId = Id.assert(jobId)
    const requestedSupportingItemId = Id.assert(requestedSupportingItem.id)
    const documentLongIdToRemove = Id.assert(documentLongId)

    await this._api.infoGathering.removeSupportingItemDocument(
      new RemoveSupportingItemDocumentRequest()
        .setId(requestedSupportingItemId)
        .setDocumentLongId(documentLongIdToRemove),
    )

    // Optimistically update the checklist item.
    requestedSupportingItem.input.removeDocument(documentLongIdToRemove)

    // Refresh the checklist.
    void this._refreshChecklist(jobId)
  }

  /**
   * Provides notes for a supporting item.
   * @param jobId
   * @param requestedSupportingItem
   * @param notes
   */
  async provideSupportingItemNotes(
    jobId: Id,
    requestedSupportingItem: RequestedSupportingItem<NotesSupportingItemInput>,
    notes: string,
  ) {
    jobId = Id.assert(jobId)
    const requestedSupportingItemId = Id.assert(requestedSupportingItem.id)

    await this._api.infoGathering.provideSupportingItemNotes(
      new ProvideSupportingItemNotesRequest()
        .setJobId(jobId)
        .setId(requestedSupportingItemId)
        .setNotes(notes),
    )

    // Optimistically update the checklist item.
    action(() => {
      requestedSupportingItem.input.text = notes
    })()

    // Refresh the checklist.
    void this._refreshChecklist(jobId)
  }

  /**
   * Completes info gathering for the job.
   *
   * @param jobId
   */
  async completeInfoGathering(jobId: Id) {
    jobId = Id.assert(jobId)
    await this._api.jobs.completeInfoGathering(
      new CompleteInfoGatheringRequest().setId(jobId),
    )

    // Since we changed the job's status by completing info gathering, we'll
    // refetch it and wait until we've observed the status changing out
    // of info gathering.
    let attempts = 0
    while (true) {
      const job = await this._submittedJobStore.fetchJob(jobId, { force: true })
      if (job?.status !== 'InfoGathering') {
        return
      }

      attempts++

      if (attempts > 30) {
        throw new Error(
          'Timed out waiting for job to transition out of info gathering',
        )
      }

      await delay(500)
    }
  }

  /**
   * Refreshes the checklist.
   *
   * @param jobId
   * @private
   */
  private _refreshChecklist(jobId: string | undefined) {
    clearMemoized(this.fetchChecklist, jobId)
    return this.fetchChecklist(jobId)
  }

  /**
   * Whenever a document request has been created, we need to reload the
   * checklist if one was already loaded.
   *
   * @param event
   * @private
   */
  @action.bound
  private _onRemoteDocumentRequestCreated(
    event: DocumentRequestSubmittedRealtimeEvent,
  ) {
    const jobId = event.getJobId()
    const checklist = this.checklists.get(jobId)
    if (!checklist) {
      return
    }

    // The item isn't in the checklist, so it may be due to a new
    // section having been added. Reload the checklist.
    void this._refreshChecklist(jobId)
  }

  /**
   * Handles a remote update to a supporting item.
   * @param event
   * @private
   */
  @action.bound
  private _onRemoteUpdate(
    event: RequestedSupportingItemUpdatedRealtimeEventProto,
  ) {
    const jobId = event.getJobId()
    const checklist = this.checklists.get(jobId)
    if (!checklist) {
      return
    }

    const itemProto = event.getRequestedSupportingItem()
    if (!itemProto) {
      return
    }

    const item = checklist.getItem(itemProto.getId())
    if (!item) {
      // The item isn't in the checklist, so it may be due to a new
      // section having been added. Reload the checklist.
      void this._refreshChecklist(jobId)
      return
    }

    // Update the item.
    item.set(mapRequestedSupportingItemProtoToProps(itemProto))
    return { checklist, item }
  }
}
